import i18n from "@/i18n";
import Decimal from 'decimal.js';
import {RESPONSES} from "@/constants";
import {notify} from "@/plugins/notification";
import router, {CONTEXT_FREE_ROUTES} from "@/router";

let _store = null;
export function setStore(store) {
    _store = store;
}

// Marker for errors rethrown out of a permissionDenied errorHandler. Using a
// WeakSet avoids mutating the error (frozen errors throw in strict mode).
const _permissionDeniedErrors = new WeakSet();

function handleAuthExpired() {
    if (!_store) return;
    if (!_store.state.authentication.isAuthorized) return;
    _store.commit("authentication/setIsAuthorized", false);
    _store.commit("personalData/setRole", null, {root: true});
    // Router guard only fires on navigation; without a hard-redirect a parallel
    // burst of 401/403 leaves the user on a frozen page until the next click.
    // Skip during boot (router push will land them on /sign-in normally) and
    // for routes that expect to run unauthenticated.
    if (!_store.state.authentication.appInitialized) return;
    const routeName = router.currentRoute.value?.name;
    if (CONTEXT_FREE_ROUTES.includes(routeName)) return;
    if (typeof window !== 'undefined') {
        window.location.assign('/sign-in/');
    }
}

export default {
    _nativeTokens: {},
    getCookie: function (name) {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        if (!cookieValue && this._nativeTokens[name]) {
            return this._nativeTokens[name];
        }
        return cookieValue;
    },

    setCookie: (name, value, seconds) => {
        const date = new Date();
        date.setTime(date.getTime() + (seconds * 1000));
        const expires = "expires=" + date.toUTCString();
        document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/`;
    },

    formatPhone: (value) => {
        // Format any user input to "+X (XXX) XXX-XX-XX" RU-style.
        // Normalizes leading 8 → 7 and supplies a leading 7 if missing so the
        // input always reads as a full international number while the user types.
        if (value == null) return "";
        let digits = String(value).replace(/\D/g, "");
        if (digits.length === 0) return "";
        if (digits.startsWith("8")) digits = "7" + digits.slice(1);
        digits = digits.slice(0, 11);
        let result = "+" + digits[0];
        if (digits.length > 1) result += " (" + digits.slice(1, Math.min(4, digits.length));
        if (digits.length >= 5) result += ") " + digits.slice(4, Math.min(7, digits.length));
        if (digits.length >= 8) result += "-" + digits.slice(7, Math.min(9, digits.length));
        if (digits.length >= 10) result += "-" + digits.slice(9, 11);
        return result;
    },

    formatTelegramHandle: (value) => {
        // Telegram usernames: 5–32 chars, [A-Za-z0-9_]. We accept shorter while
        // typing and just strip anything invalid, prefixing with @.
        if (value == null) return "";
        const cleaned = String(value).replace(/[^A-Za-z0-9_]/g, "").slice(0, 32);
        return cleaned ? "@" + cleaned : "";
    },

    deleteCookie: (name) => {
        const domainParts = location.hostname.split('.');
        if (domainParts.length >= 2) {
            const baseDomain = '.' + domainParts.slice(-2).join('.');
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${baseDomain}`;
        }
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    },

    defaultErrorHandler: (data) => {
        notify(i18n.global.t("Oops! Something went wrong..."), "red", 3000);
        return {successfully: false, detail: data?.detail};
    },

    sendRequestToGetJson: function (data) {
        const {url, options, successfulHandler, errorHandler} = data;
        // Silent short-circuit: if a caller tries to send a model-API request
        // without a role header (e.g. polling after logout / expired cookie),
        // don't hit the server — it would answer with 400 "Missing role header"
        // and trigger the generic "Oops, something went wrong" toast.
        const headerRole = options?.headers?.role;
        if (url && url.includes('/api/model/') && (!headerRole || headerRole === 'null')) {
            return Promise.resolve({successfully: false, missingRole: true});
        }
        return fetch(url, options)
            .then(response => {
                if (response.status === 503) {
                    return response.json().then(json => {
                        const msg = json.message || i18n.global.t("The server is undergoing maintenance. Please try again later.");
                        notify(msg, "red", 5000);
                        return {successfully: false, maintenance: true};
                    }).catch(() => {
                        notify(i18n.global.t("The server is undergoing maintenance. Please try again later."), "red", 5000);
                        return {successfully: false, maintenance: true};
                    });
                }
                if (response.status === 401) {
                    handleAuthExpired();
                    return {successfully: false, authExpired: true};
                }
                if (response.status === 403) {
                    return response.json().catch(() => ({})).then(json => {
                        const detail = json.detail || "";
                        if (detail.includes("plan") || detail.includes("plugin") || detail.includes("expired")) {
                            notify(i18n.global.t("Your plan has expired or does not include this feature"), "red", 5000);
                            return {successfully: false, planExpired: true};
                        }
                        // DRF returns 403 (not 401) on stale session — treat as auth expiry.
                        if (/credentials were not provided|Учетные данные не были предоставлены|CSRF/i.test(detail)) {
                            handleAuthExpired();
                            return {successfully: false, authExpired: true};
                        }
                        notify(i18n.global.t("You don't have permission to perform this action"), "red", 4000);
                        return {successfully: false, permissionDenied: true, detail};
                    });
                }
                return response.json()
            })
            .then(data => {
                if (data.maintenance || data.authExpired || data.planExpired) return data;
                if (data.permissionDenied) {
                    // Toast already fired above; skip defaultErrorHandler to avoid double-toast.
                    if (errorHandler) {
                        try { return errorHandler(data); }
                        catch (e) {
                            if (e && typeof e === 'object') _permissionDeniedErrors.add(e);
                            throw e;
                        }
                    }
                    return data;
                }
                if (data.result === RESPONSES.SUCCESSFULLY) {
                    if (successfulHandler) return successfulHandler(data);
                    return {successfully: true};
                }
                if (errorHandler) return errorHandler(data);
                return this.defaultErrorHandler(data);
            })
            .catch(error => {
                if (error && typeof error === 'object' && _permissionDeniedErrors.has(error)) {
                    return {successfully: false, permissionDenied: true};
                }
                console.error(error.stack);
                if (this.isProbablyOffline(error)) {
                    notify(i18n.global.t("Oops! Looks like there’s a problem with your internet connection..."), "red", 3000);
                    return {successfully: false, detail: data?.detail};
                }
                return this.defaultErrorHandler();
            })
    },

    sendRequestToGetBlob: function (data) {
        const {url, options, successfulHandler} = data;
        return fetch(url, options)
            .then(response => {
                return response.blob();
            })
            .then(blob => {
                return successfulHandler(blob);
            })
            .catch(error => {
                console.error(error.stack);
                if (this.isProbablyOffline(error)) {
                    notify(i18n.global.t("Oops! Looks like there’s a problem with your internet connection..."), "red", 3000);
                    return {successfully: false, detail: data?.detail};
                }
                return this.defaultErrorHandler();
            })
    },

    isProbablyOffline(error) {
        return !navigator.onLine || !error.response || error.message === 'Network Error' || error.message === 'Failed to fetch';
    },

    getTransactionNameByOperations(operations) {
        if (operations.length === 0) return "-";
        if (operations.length === 1) return operations[0].good.name;
        return `${operations[0].good.name}, ...`;
    },

    getAmountOfDaysBetweenDates(startDateString, endDateString) {
        const startDate = new Date(startDateString);
        const endDate = new Date(endDateString);
        const diffInMs = endDate - startDate;
        const millisecondsPerDay = 1000 * 60 * 60 * 24;
        return Math.floor(diffInMs / millisecondsPerDay);
    },
    formatScheduleTimeRange(start, end) {
        if (!start || !end) return "";
        const startHour = parseInt(String(start).split(":")[0], 10);
        const endHour = parseInt(String(end).split(":")[0], 10);
        return `${startHour}/${endHour}`;
    },
    formatDate(languageCode, dateString) {
        const date = new Date(dateString);
        if (languageCode === "en") return `${date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`;
        if (languageCode === "ru") return `${date.toLocaleDateString('ru-RU', { year: 'numeric', month: 'short', day: 'numeric' })}`;
    },
    formatDateTime(languageCode, dateString) {
        const date = new Date(dateString);
        let formatter;
        if (languageCode === "ru") {
            formatter = new Intl.DateTimeFormat('ru-RU', {hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'long', year: 'numeric'});
        } else if (languageCode === "en") {
            formatter = new Intl.DateTimeFormat('en-US', {hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'long', year: 'numeric'});
        }
        const parts = formatter.formatToParts(date);
        const components = {};
        for (const part of parts) {
            components[part.type] = part.value;
        }

        const hours = components.hour.padStart(2, '0');
        const minutes = components.minute.padStart(2, '0');
        const day = components.day;
        const month = components.month;
        const year = components.year;

        return `${hours}:${minutes} ${day} ${month} ${year} ${i18n.global.t("year")}`;
    },
    dropTimezoneFromDateTime(dateString) {
        const date = new Date(dateString);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    },
    isDifferenceWithinOneYear(date1, date2) {
        const [earlierDate, laterDate] = date1 < date2 ? [date1, date2] : [date2, date1];        
        const yearDifference = laterDate.getFullYear() - earlierDate.getFullYear();
        const monthDifference = laterDate.getMonth() - earlierDate.getMonth();
        const dayDifference = laterDate.getDate() - earlierDate.getDate();
      
        if (yearDifference < 1) return true;
        return yearDifference === 1 && (monthDifference < 0 || (monthDifference === 0 && dayDifference <= 0));
    },
    isToday(dateString, timezone) {
        const formatter = new Intl.DateTimeFormat("en-CA", {timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"})
        const today = formatter.format(new Date());
        return dateString.split("T")[0] === today;
    },
    isYesterday(dateString, timezone) {
        const formatter = new Intl.DateTimeFormat("en-CA", {timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"})
        const today = new Date();
        let yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        yesterday = formatter.format(yesterday);
        return dateString.split("T")[0] === yesterday;
    },
    isStartAndEndOfMonth(start, end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
    
        const sameMonth = startDate.getMonth() === endDate.getMonth();
        const sameYear = startDate.getFullYear() === endDate.getFullYear();
    
        const isStartOfMonth = startDate.getDate() === 1;
    
        const lastDay = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();
        const isEndOfMonth = endDate.getDate() === lastDay;
    
        return sameMonth && sameYear && isStartOfMonth && isEndOfMonth;
    },
    isStartAndEndOfYear(start, end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
    
        const sameYear = startDate.getFullYear() === endDate.getFullYear();    
        const isStartOfYear = startDate.getMonth() === 0 && startDate.getDate() === 1;    
        const isEndOfYear = endDate.getMonth() === 11 && endDate.getDate() === 31;
    
        return sameYear && isStartOfYear && isEndOfYear;
    },
    getTime(dateString) {
        return dateString.split("T")[1].split(".")[0];
    },
    getCurrentDate(timezone = null) {
        const date = new Date();
        if (timezone) {
            const formatter = new Intl.DateTimeFormat("en-CA", {timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"})
            return formatter.format(date);
        }

        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
    },
    getWeekDayByDate(dateString, language) {
        const date = new Date(dateString);
        let verboseWeekDays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
        if (language === "ru") verboseWeekDays = ["ВС", "ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ"];
        return verboseWeekDays[date.getDay()];
    },
    getFirstDayOfCurrentMonth() {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        return firstDay.toLocaleDateString("en-CA");
    },
    getCurrentMonthPeriod(timezone = null) {
        let today = new Date();
        if (timezone) {
            const formatter = new Intl.DateTimeFormat("en-CA", {timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"})
            today = new Date(formatter.format(new Date()));
        }

        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0); 
        const formattedStart = `${startOfMonth.getFullYear()}-${String(startOfMonth.getMonth() + 1).padStart(2, '0')}-${String(startOfMonth.getDate()).padStart(2, '0')}`;
        const formattedEnd = `${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}`;
    
        return {start: formattedStart, end: formattedEnd};
    },
    getCurrentYearPeriod(timezone = null) {
        let today = new Date();
        if (timezone) {
            const formatter = new Intl.DateTimeFormat("en-CA", {timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"})
            today = new Date(formatter.format(new Date()));
        }

        const startOfYear = new Date(today.getFullYear(), 0, 1);
        const endOfYear = new Date(today.getFullYear(), 11, 31);
        const formattedStart = `${startOfYear.getFullYear()}-${String(startOfYear.getMonth() + 1).padStart(2, '0')}-${String(startOfYear.getDate()).padStart(2, '0')}`;
        const formattedEnd = `${endOfYear.getFullYear()}-${String(endOfYear.getMonth() + 1).padStart(2, '0')}-${String(endOfYear.getDate()).padStart(2, '0')}`;
    
        return {start: formattedStart, end: formattedEnd};
    },
    getCurrentMonthAndYear(language, timezone = null) {
        let today = new Date();
        if (timezone) {
            const formatter = new Intl.DateTimeFormat("en-CA", {timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"})
            today = new Date(formatter.format(new Date()));
        }
        let monthName = new Intl.DateTimeFormat('en-US', {year: "numeric", month: 'long'}).format(today);
        if (language === "ru") {
            monthName = new Intl.DateTimeFormat('ru-RU', {year: "numeric", month: 'long'}).format(today);
            return monthName;
        }
        return this.capitalizeFirstLetter(monthName);
    },
    getCurrentYear(yearVerbose, timezone = null) {
        let today = new Date();
        if (timezone) {
            const formatter = new Intl.DateTimeFormat("en-CA", {timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit"})
            today = new Date(formatter.format(new Date()));
        }
        return `${today.getFullYear()} ${yearVerbose}`;
    },
    getMonthAndYear(dateString, language, monthMode="long") {
        const date = new Date(dateString);
        let formatter = new Intl.DateTimeFormat('en-US', {year: 'numeric', month: monthMode});
        if (language === "ru") {
            formatter = new Intl.DateTimeFormat('ru-RU', {year: 'numeric', month: monthMode});
            return formatter.format(date)
        }
        return this.capitalizeFirstLetter(formatter.format(date));
    },
    getYear(dateString) {
        const date = new Date(dateString);
        return `${date.getFullYear()} ${i18n.global.t("year")}`;
    },
    getPreviousDay(dateString) {
        const date = new Date(dateString);
        date.setDate(date.getDate() - 1);
        return date.toISOString().split('T')[0];
    },
    getNextDay(dateString) {
        const date = new Date(dateString);
        date.setDate(date.getDate() + 1);
        return date.toISOString().split('T')[0];
    },
    getFirstDayOfPreviousMonth(dateString) {
        const date = new Date(dateString);    
        const firstDayPreviousMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);

        const year = firstDayPreviousMonth.getFullYear();
        const month = String(firstDayPreviousMonth.getMonth() + 1).padStart(2, '0');
        const day = String(firstDayPreviousMonth.getDate()).padStart(2, '0');
    
        return `${year}-${month}-${day}`;
    },
    getLastDayOfPreviousMonth(dateString) {
        const date = new Date(dateString);
        date.setDate(1);
        date.setMonth(date.getMonth() - 1);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const lastDay = new Date(year, date.getMonth() + 1, 0).getDate();

        return `${year}-${month}-${lastDay}`;
    },
    getFirstDayOfNextMonth(dateString) {
        const date = new Date(dateString);    
        const firstDayPreviousMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);

        const year = firstDayPreviousMonth.getFullYear();
        const month = String(firstDayPreviousMonth.getMonth() + 1).padStart(2, '0');
        const day = String(firstDayPreviousMonth.getDate()).padStart(2, '0');
    
        return `${year}-${month}-${day}`;
    },
    getLastDayOfNextMonth(dateString) {
        const date = new Date(dateString);
        date.setDate(1);
        date.setMonth(date.getMonth() + 1);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const lastDay = new Date(year, date.getMonth() + 1, 0).getDate();

        return `${year}-${month}-${lastDay}`;
    },
    getFirstDayOfPreviousYear(dateString) {
        const date = new Date(dateString);
        const firstDayPreviousYear = new Date(date.getFullYear() - 1, 0, 1);

        const year = firstDayPreviousYear.getFullYear();
        const month = String(firstDayPreviousYear.getMonth() + 1).padStart(2, '0');
        const day = String(firstDayPreviousYear.getDate()).padStart(2, '0');
    
        return `${year}-${month}-${day}`;
    },
    getLastDayOfPreviousYear(dateString) {
        const date = new Date(dateString);    
        const lastDayPreviousYear = new Date(date.getFullYear() - 1, 11, 31);
    
        const year = lastDayPreviousYear.getFullYear();
        const month = String(lastDayPreviousYear.getMonth() + 1).padStart(2, '0');
        const day = String(lastDayPreviousYear.getDate()).padStart(2, '0');
    
        return `${year}-${month}-${day}`;
    },
    getFirstDayOfNextYear(dateString) {
        const date = new Date(dateString);
        const firstDayPreviousYear = new Date(date.getFullYear() + 1, 0, 1);

        const year = firstDayPreviousYear.getFullYear();
        const month = String(firstDayPreviousYear.getMonth() + 1).padStart(2, '0');
        const day = String(firstDayPreviousYear.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    },
    getLastDayOfNextYear(dateString) {
        const date = new Date(dateString);    
        const lastDayPreviousYear = new Date(date.getFullYear() + 1, 11, 31);
    
        const year = lastDayPreviousYear.getFullYear();
        const month = String(lastDayPreviousYear.getMonth() + 1).padStart(2, '0');
        const day = String(lastDayPreviousYear.getDate()).padStart(2, '0');
    
        return `${year}-${month}-${day}`;
    },
    capitalizeFirstLetter(str) {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    },
    getDeviceType() {
        const ua = navigator.userAgent.toLowerCase();
        const isIpad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
        if (isIpad || /ipad|tablet|playbook|silk/.test(ua)) return "tablet";
        if (/mobile/.test(ua)) return "phone";
        if (/android/.test(ua) && !/mobile/.test(ua)) return "tablet";
        return "desktop";
    },
    formatNumberWithSpaces(number) {
        if (number === null || number === undefined || isNaN(number)) return "";
        return String(number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    isNumeric(value) {
        return /^-?\d+(\.\d+)?$/.test(value);
    },
    isEmptyObject(obj) {
        return Object.keys(obj).length === 0;
    },
    formatCharacteristics(characteristics) {
        return Object.entries(characteristics)
                     .filter(([key, value]) => key && value !== null && value !== undefined && value !== "")
                     .map(([key, value]) => `${key}: ${value}`).join(" | ");
    },
    setDataToLocalStorage(property, data) {
        localStorage.setItem(property, data);
    },
    getDataFromLocalStorage(property) {
        return localStorage.getItem(property);
    },
    deleteDataFromLocalStorage(property) {
        localStorage.removeItem(property);
    },
    discountedPrice(priceWithoutDiscount, discount) {
        if (!priceWithoutDiscount || !discount) return priceWithoutDiscount;
        const price = new Decimal(priceWithoutDiscount);
        const discountValue = new Decimal(discount).div(100);
        const finalPrice = price.minus(price.mul(discountValue));
        return finalPrice.toFixed(2);
    },
    formatPhoneNumber(phone) {
        if (phone === null || phone === undefined) return "";
        const cleaned = phone.replace(/\D/g, '');
        let result = "";

        if (cleaned.startsWith("7")) {
            result = '+7';
            if (cleaned.length > 1) {result += ` (${cleaned.slice(1, 4)}`}
            if (cleaned.length > 4) {result += `) ${cleaned.slice(4, 7)}`}
            if (cleaned.length > 7) {result += `-${cleaned.slice(7, 9)}`}
            if (cleaned.length > 9) {result += `-${cleaned.slice(9, 11)}`}
        } else if (cleaned.startsWith("1")) {
            result = "+1";
            if (cleaned.length > 1) {result += ` (${cleaned.slice(1, 4)}`}
            if (cleaned.length > 4) {result += `) ${cleaned.slice(4, 7)}`}
            if (cleaned.length > 7) {result += `-${cleaned.slice(7, 11)}`}
        } else if (cleaned.startsWith("375")) {
            result = '+375';
            if (cleaned.length > 3) {result += ` (${cleaned.slice(3, 5)}`}
            if (cleaned.length > 5) {result += `) ${cleaned.slice(5, 8)}`}
            if (cleaned.length > 8) {result += `-${cleaned.slice(8, 10)}`}
            if (cleaned.length > 10) {result += `-${cleaned.slice(10, 12)}`}
        } else if (cleaned.startsWith("998")) {
            result = '+998';
            if (cleaned.length > 3) {result += ` (${cleaned.slice(3, 5)}`}
            if (cleaned.length > 5) {result += `) ${cleaned.slice(5, 8)}`}
            if (cleaned.length > 8) {result += `-${cleaned.slice(8, 10)}`}
            if (cleaned.length > 10) {result += `-${cleaned.slice(10, 12)}`}
        } else if (cleaned.startsWith("86")) {
            result = '+86';
            if (cleaned.length > 2) {result += ` ${cleaned.slice(2, 5)}`}
            if (cleaned.length > 5) {result += `-${cleaned.slice(5, 9)}`}
            if (cleaned.length > 9) {result += `-${cleaned.slice(9, 13)}`}
        } else if (cleaned.startsWith("994")) {
            result = '+994';
            if (cleaned.length > 3) {result += ` ${cleaned.slice(3, 5)}`}
            if (cleaned.length > 5) {result += ` ${cleaned.slice(5, 8)}`}
            if (cleaned.length > 8) {result += `-${cleaned.slice(8, 10)}`}
            if (cleaned.length > 10) {result += `-${cleaned.slice(10, 12)}`}
        } else if (cleaned.startsWith("90")) {
            result = '+90';
            if (cleaned.length > 2) {result += ` (${cleaned.slice(2, 5)}`}
            if (cleaned.length > 5) {result += `) ${cleaned.slice(5, 8)}`}
            if (cleaned.length > 8) {result += `-${cleaned.slice(8, 10)}`}
            if (cleaned.length > 10) {result += `-${cleaned.slice(10, 12)}`}
        } else if (cleaned.startsWith("995")) {
            result = '+995';
            if (cleaned.length > 3) {result += ` (${cleaned.slice(3, 6)}`}
            if (cleaned.length > 6) {result += `) ${cleaned.slice(6, 8)}`}
            if (cleaned.length > 8) {result += `-${cleaned.slice(8, 10)}`}
            if (cleaned.length > 10) {result += `-${cleaned.slice(10, 12)}`}
        } else if (cleaned.startsWith("66")) {
            result = '+66';
            if (cleaned.length > 2) {result += ` ${cleaned.slice(2, 4)}`}
            if (cleaned.length > 4) {result += ` ${cleaned.slice(4, 7)}`}
            if (cleaned.length > 7) {result += `-${cleaned.slice(7, 11)}`}
        } else if (cleaned.length > 0) {
            result = `+${cleaned}`;
        }
        return result.trim();
    },
    validatePhone(phoneNumber) {
        const clearPhone = phoneNumber.replace(/\D/g, '');

        if (clearPhone.startsWith('7')) return clearPhone.length === 11;
        if (clearPhone.startsWith('1')) return clearPhone.length === 11;
        if (clearPhone.startsWith('375')) return clearPhone.length === 12;
        if (clearPhone.startsWith('998')) return clearPhone.length === 12;
        if (clearPhone.startsWith('86')) return clearPhone.length === 13;
        if (clearPhone.startsWith('994')) return clearPhone.length === 12;
        if (clearPhone.startsWith('90')) return clearPhone.length === 12;
        if (clearPhone.startsWith('995')) return clearPhone.length === 12;
        if (clearPhone.startsWith('66')) return clearPhone.length === 11;

        return false;
    },
    validateEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    },
    keepOnlyNumbers(string) {
        return string.replace(/\D/g, '');
    },
    orderPaymentMethods(paymentMethods, paymentMethodOrder) {
        const result = {};
        if (!paymentMethods || !paymentMethodOrder) return result;
        for (const key of paymentMethodOrder) {
            if (!paymentMethods[key]) continue;
            result[key] = paymentMethods[key];
        }
        for (const key of paymentMethodOrder) {
            if (paymentMethods[key]) continue;
            result[key] = paymentMethods[key];
        }
        return result;
    },
    isNativeApp() {
        return !!window.Capacitor?.isNativePlatform?.() && window.Capacitor.isNativePlatform()
    },
    isPWA() {
        if (typeof window === 'undefined') return false

        const mq = window.matchMedia || window.msMatchMedia;
        const displayModes = ['standalone', 'fullscreen', 'minimal-ui']
        const isDisplayModePWA = mq ? displayModes.some(mode => mq(`(display-mode: ${mode})`).matches) : false;

        const isIosStandalone = window.navigator.standalone === true;
        return this.isNativeApp() || isDisplayModePWA || isIosStandalone;
    },
    getAllDayDatesAndNumbersOfMonth(monthYM) {
        const [year, month] = monthYM.split('-').map(Number);
        const daysInMonth = new Date(year, month, 0).getDate();

        const result = [];
        for (let day = 1; day <= daysInMonth; day++) {
            result.push({date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, day: day});
        }
        return result;
    },
    getMonthBounds(monthYM) {
        const [year, month] = monthYM.split('-').map(Number);
        const end = new Date(year, month, 0);
        const startYMD = `${year}-${String(month).padStart(2, '0')}-01`;
        const endYMD = `${year}-${String(month).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
        return [startYMD, endYMD];
    },
};